/**
 * WelcomePanel Component
 *
 * 380×454 flip card shown over the closed board before the player opens it.
 * Replaces StartPanel with a two-faced design:
 *
 * Face A — Welcome: puzzle name + Start/Resume/Review CTA + SAMPLE PUZZLES button
 * Face B — Sample Puzzles: 5×5 grid with progress indicators + Back button
 *
 * CTA variant is determined by checking localStorage for game state,
 * avoiding any dependency on GameContext.
 *
 * Rise animation: when onStart fires, the panel translates up out of view,
 * clipped by the Board mainBlock overflow.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import styles from './WelcomePanel.module.css';
import Phrase from '../utils/phraseMaker';
import NumberGlyphs from '../svg/numbers';
import SvgFrownyFace from '../svg/FrownyFace';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SamplePuzzle {
  id: string;
  name: string;
  puzzle_number: number | null;
  puzzle_text: string;
  puzzle_json: import('../types').PuzzleJSON | null;
  date: string;
}

interface SelectedPuzzle {
  id: string;
  name: string;
  puzzleNumber?: number | null;
  puzzleText: string;
  puzzleJson?: import('../types').PuzzleJSON | null;
  date: string;
  sampleIndex?: number;
}

type CtaVariant = 'start' | 'resume' | 'review';

interface WelcomePanelProps {
  /** Currently selected puzzle info (null = show sample select as initial face) */
  selectedPuzzle: SelectedPuzzle | null;
  /** Display name for the selected puzzle */
  puzzleDisplayName: string;
  /** Called when Start/Resume/Review is clicked */
  onStart: () => void;
  /** Called when a sample puzzle is selected from the grid */
  onSelectPuzzle: (puzzle: SelectedPuzzle) => void;
  /** Called when Reset is clicked (optional — not rendered in current UI) */
  onReset?: () => void;
  /** Rise phase: 'idle' = static, 'rising' = translateY up, 'risen' = fully risen, 'dropping' = animating back down */
  risePhase: 'idle' | 'rising' | 'risen' | 'dropping';
  /** When true, renders as inline card (no absolute positioning / rise clip).
   *  Used when WelcomePanel is placed inside LoginBoard's doorRect. */
  inline?: boolean;
  /** Board scale factor — used to counter-scale for crisp text rendering */
  boardScale?: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const OUTER_HEIGHT = 620;
const RISE_DURATION = 800;
const TRAVEL_BUFFER = 10; // extra pixels to ensure fully out of view

// ---------------------------------------------------------------------------
// CTA variant helper — checks localStorage without GameContext
// ---------------------------------------------------------------------------

interface PuzzleState {
  variant: CtaVariant;
  gameWon: boolean | null;
  finalScore: number | null;
  credits: number;
}

function getPuzzleState(puzzleDate: string | undefined): PuzzleState {
  const defaultState: PuzzleState = { variant: 'start', gameWon: null, finalScore: null, credits: 10 };
  if (!puzzleDate) return defaultState;
  try {
    const saved = localStorage.getItem(`this-and-that-game-${puzzleDate}`);
    if (!saved) return defaultState;
    const parsed = JSON.parse(saved);
    const state = parsed.state ?? parsed;
    const credits = typeof state.credits === 'number' ? state.credits : 10;
    if (state.finalAnswerSubmitted) {
      return {
        variant: 'review',
        gameWon: state.gameWon ?? null,
        finalScore: state.finalScore ?? null,
        credits,
      };
    }
    const hasSpentCredits = credits < 10;
    const hasGuesses = state.guessAttempts
      && Object.values(state.guessAttempts).some((v: unknown) => (v as number) > 0);
    if (hasSpentCredits || hasGuesses) return { variant: 'resume', gameWon: null, finalScore: null, credits };
    return defaultState;
  } catch {
    return defaultState;
  }
}

// Backward-compatible wrapper used by sample circles
function getCtaVariant(puzzleDate: string | undefined): CtaVariant {
  return getPuzzleState(puzzleDate).variant;
}

// ---------------------------------------------------------------------------
// Progress indicator helper
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WelcomePanel: React.FC<WelcomePanelProps> = ({
  selectedPuzzle,
  puzzleDisplayName,
  onStart,
  onSelectPuzzle,
  risePhase,
  inline = false,
  boardScale = 1,
}) => {

  // Flip state: false = Face A (Welcome), true = Face B (Samples)
  // Start on Face B if no puzzle is selected
  const [flipped, setFlipped] = useState(!selectedPuzzle);

  // Sample puzzles data
  const [puzzles, setPuzzles] = useState<SamplePuzzle[]>([]);
  const [loading, setLoading] = useState(true);

  // Pending display state — overrides during flip animation so Face A shows
  // the NEWLY selected puzzle's context, not the previous one
  const [pendingDisplay, setPendingDisplay] = useState<{
    puzzleState: PuzzleState;
    displayName: string;
  } | null>(null);

  // Puzzle state — computed every render (cheap localStorage read).
  // Cannot memo on date alone because reset changes state without changing the date.
  const rawPuzzleState = getPuzzleState(selectedPuzzle?.date);
  const puzzleState = pendingDisplay?.puzzleState ?? rawPuzzleState;
  const ctaVariant = puzzleState.variant;
  const effectiveDisplayName = pendingDisplay?.displayName ?? puzzleDisplayName;

  // Flip to Face A when a puzzle gets selected
  useEffect(() => {
    if (selectedPuzzle) setFlipped(false);
  }, [selectedPuzzle]);

  // Fetch sample puzzles
  useEffect(() => {
    const fetchData = async () => {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) {
        setLoading(false);
        return;
      }

      const headers = {
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseKey}`,
      };

      try {
        const puzzleResp = await fetch(
          `${supabaseUrl}/rest/v1/puzzles_admin?is_sample=eq.true&select=id,name,puzzle_number,puzzle_text,puzzle_json,date&order=date.asc`,
          { headers }
        );

        if (!puzzleResp.ok) { setLoading(false); return; }
        const rows: SamplePuzzle[] = await puzzleResp.json();
        setPuzzles(rows);

      } catch (err) {
        console.warn('WelcomePanel: fetch failed', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Pending puzzle ref — used to delay onSelectPuzzle until flip animation completes
  const pendingPuzzleRef = useRef<{ puzzle: SamplePuzzle; index: number } | null>(null);

  const handlePuzzleSelect = useCallback((puzzle: SamplePuzzle, index: number) => {
    // Set pending display state so Face A shows the NEW puzzle's context during flip
    setPendingDisplay({
      puzzleState: getPuzzleState(puzzle.date),
      displayName: `SAMPLE PUZZLE ${index + 1}`,
    });
    // Flip to Face A first, then fire onSelectPuzzle after animation
    pendingPuzzleRef.current = { puzzle, index };
    setFlipped(false);
    // Fallback: if flip was already on Face A (no animation), fire immediately
    if (!flipped) {
      pendingPuzzleRef.current = null;
      setPendingDisplay(null);
      onSelectPuzzle({
        id: puzzle.id,
        name: puzzle.name,
        puzzleNumber: puzzle.puzzle_number,
        puzzleText: puzzle.puzzle_text,
        puzzleJson: puzzle.puzzle_json,
        date: puzzle.date,
        sampleIndex: index + 1,
      });
      return;
    }
  }, [onSelectPuzzle, flipped]);

  // After flip animation completes, fire the pending puzzle selection
  const handleFlipTransitionEnd = useCallback(() => {
    const pending = pendingPuzzleRef.current;
    if (pending && !flipped) {
      pendingPuzzleRef.current = null;
      setPendingDisplay(null);
      onSelectPuzzle({
        id: pending.puzzle.id,
        name: pending.puzzle.name,
        puzzleNumber: pending.puzzle.puzzle_number,
        puzzleText: pending.puzzle.puzzle_text,
        puzzleJson: pending.puzzle.puzzle_json,
        date: pending.puzzle.date,
        sampleIndex: pending.index + 1,
      });
    }
  }, [onSelectPuzzle, flipped]);

  const ctaLabel = ctaVariant === 'start'
    ? 'START PUZZLE'
    : ctaVariant === 'resume'
      ? 'RESUME PUZZLE'
      : 'REVIEW PUZZLE';

  const rising = risePhase === 'rising';
  const risen = risePhase === 'risen';
  const dropping = risePhase === 'dropping';

  // Review state details
  const isReviewWin = ctaVariant === 'review' && puzzleState.gameWon === true;
  const isReviewLose = ctaVariant === 'review' && puzzleState.gameWon === false;
  const finalScore = puzzleState.finalScore ?? 0;
  const solveBonus = 10;
  const creditBonus = Math.max(0, finalScore - solveBonus);

  // Greeting text per variant
  const greetingText = ctaVariant === 'start' ? "LET'S\nGO!"
    : ctaVariant === 'resume' ? 'HELLO\nAGAIN!'
    : isReviewWin ? 'SMASHING!'
    : 'SO CLOSE!';
  const greetingFontSize = ctaVariant === 'review' ? 76 : 96; // two-line: 74 * 1.3 ≈ 96
  const greetingLineHeight = ctaVariant === 'review' ? 1.0 : 0.85;

  // Score number glyph for review/win
  const ScoreGlyph = isReviewWin ? NumberGlyphs[Math.min(20, Math.max(0, finalScore))] : null;

  // Shared flip card content (used in both inline and overlay modes)
  const flipCardContent = (
    <div className={styles.flipPerspective}>
      <div
        className={`${styles.flipCard}${flipped ? ` ${styles.flipped}` : ''}`}
        onTransitionEnd={(e) => {
          if (e.propertyName === 'transform' && e.target === e.currentTarget) {
            handleFlipTransitionEnd();
          }
        }}
      >
        {/* Face A: Welcome / Resume / Review */}
        <div className={`${styles.flipFace} ${styles.welcomeFace}`}>
          {/* Puzzle title — 24px from top */}
          <span className={styles.puzzleName} style={{ marginTop: 0 }}>
            {effectiveDisplayName}
          </span>

          {/* Greeting text */}
          <div style={{
            position: 'absolute',
            top: ctaVariant === 'review' ? 53 : 122,
            left: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}>
            <Phrase
              text={greetingText}
              fontSize={greetingFontSize}
              lineHeight={greetingLineHeight}
              textAnchor="middle"
              shadowColor="#514b44"
            />
          </div>

          {/* Review mode: score display */}
          {isReviewWin && (
            <>
              {/* Gold score circle */}
              <div style={{
                position: 'absolute',
                top: 160,
                left: (380 - 162) / 2,
                width: 162,
                height: 162,
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: '#d1930b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {ScoreGlyph && (
                    <ScoreGlyph style={{ display: 'block', transform: 'scale(0.95)' }} />
                  )}
                </div>
              </div>
              {/* Score breakdown */}
              <div style={{
                position: 'absolute',
                top: 350,
                left: 0,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                gap: 24,
                fontFamily: "'Inter', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: '#f7e5c5',
              }}>
                <span>SOLVE BONUS: {solveBonus}</span>
                <span>CREDIT BONUS: {creditBonus}</span>
              </div>
            </>
          )}

          {isReviewLose && (
            <>
              {/* Frowny face circle */}
              <div style={{
                position: 'absolute',
                top: 160,
                left: (380 - 162) / 2,
                width: 162,
                height: 162,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <SvgFrownyFace width={162} height={162} />
              </div>
              {/* Try again text */}
              <div style={{
                position: 'absolute',
                top: 350,
                left: 0,
                width: '100%',
                textAlign: 'center',
                fontFamily: "'Inter', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: '#f7e5c5',
              }}>
                TRY AGAIN TOMORROW
              </div>
            </>
          )}

          {/* CTA button — 406px from top */}
          <button
            className={styles.ctaBtn}
            onClick={onStart}
            style={{ position: 'absolute', top: 406, left: (380 - 280) / 2 }}
          >
            {ctaLabel}
          </button>

          {/* Back to Samples — 478px from top */}
          <button
            className={styles.secondaryBtn}
            onClick={() => setFlipped(true)}
            style={{ position: 'absolute', top: 478, left: (380 - 242) / 2 }}
          >
            BACK TO SAMPLES
            <span className={styles.betaTag}>(BETA)</span>
          </button>
        </div>

        {/* Face B: Sample Puzzles */}
        <div className={`${styles.flipFace} ${styles.faceBack} ${styles.samplesFace}`}>
          <p className={styles.samplesTitle}>SAMPLE PUZZLES</p>
          <p className={styles.samplesSubtitle}>Daily puzzles coming soon! Meanwhile, try the sample puzzles below.</p>
          {loading ? (
            <span className={styles.loading}>Loading...</span>
          ) : (
            <div className={styles.circleGrid}>
              {Array.from({ length: 25 }, (_, i) => {
                const p = puzzles[i];

                if (!p) {
                  return <div key={i} className={styles.circlePlaceholder} />;
                }

                const variant = getCtaVariant(p.date);
                const className = variant === 'review'
                  ? styles.circleCompleted
                  : variant === 'resume'
                    ? styles.circleInProgress
                    : styles.circle;

                return (
                  <button
                    key={p.id}
                    className={className}
                    style={variant === 'start' ? { background: '#1f7d7e' } : undefined}
                    onClick={() => handlePuzzleSelect(p, i)}
                  >
                    <span>{i + 1}</span>
                  </button>
                );
              })}
            </div>
          )}

        </div>
      </div>
    </div>
  );

  // Inline mode: render flip card directly (for use inside LoginBoard's doorRect)
  if (inline) {
    return flipCardContent;
  }

  // Measure the card to compute rise travel distance dynamically (desktop only).
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [innerTravel, setInnerTravel] = useState((OUTER_HEIGHT + TRAVEL_BUFFER) * boardScale);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useEffect(() => {
    if (isMobile || !wrapperRef.current) return;
    const card = wrapperRef.current.firstElementChild as HTMLElement | null;
    if (!card) return;
    const cardHeight = card.offsetHeight;
    const clipHeight = OUTER_HEIGHT * boardScale;
    const topOffset = Math.floor((clipHeight - cardHeight) / 2);
    setInnerTravel(topOffset + cardHeight + TRAVEL_BUFFER);
  });

  const exiting = rising || risen;

  // Desktop: slide up out of the clip container.
  // Mobile: flip on X-axis to edge-on (90°) — effectively invisible.
  const FLIP_DURATION = 500;
  const riseStyle: React.CSSProperties = isMobile
    ? {
        perspective: 1200,
        transition: rising
          ? `transform ${FLIP_DURATION}ms ease-in, opacity ${FLIP_DURATION}ms ease-in`
          : dropping
            ? `transform ${FLIP_DURATION}ms ease-out, opacity ${FLIP_DURATION}ms ease-out`
            : 'none',
        transform: exiting ? 'rotateX(90deg) scale(0.9)' : 'rotateX(0deg) scale(1)',
        opacity: exiting ? 0 : 1,
        transformOrigin: 'center center',
      }
    : {
        transition: rising
          ? `transform ${RISE_DURATION}ms ease-in`
          : dropping
            ? `transform ${RISE_DURATION}ms ease-out`
            : 'none',
        transform: exiting ? `translateY(-${innerTravel}px)` : 'translateY(0)',
      };

  // Scaled positions — outerClip is now inside the outer fixed div
  // (BOARD_WIDTH*scale × BOARD_HEIGHT*scale), not inside the 1000px board
  const s = boardScale;
  const outerStyle: React.CSSProperties = isMobile
    ? { position: 'absolute', left: 0, top: 0, width: 1000 * s, height: 1000 * s, zIndex: 60, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }
    : { position: 'absolute', left: 15 * s, top: 200 * s, width: 970 * s, height: 620 * s, overflow: 'hidden', zIndex: 60, pointerEvents: 'none' };

  // Mobile: 1.5x larger relative to board for readability
  const cardZoom = isMobile ? s * 1.5 : s;

  return (
    <div style={outerStyle}>
      <div
        ref={wrapperRef}
        className={styles.riseWrapper}
        style={riseStyle}
      >
        <div style={{ zoom: cardZoom, pointerEvents: 'auto' }}>
          {flipCardContent}
        </div>
      </div>
    </div>
  );
};

export default React.memo(WelcomePanel);
